import React from 'react';
import { Grid, Card, CardContent, Typography } from '@mui/material';
import { IDocumentStats } from '../../../Types/Docs';

interface QueryStatsCardsProps {
  stats: IDocumentStats;
  isLoading: boolean;
  onLibraryClick: () => void;
}

const StatsCard: React.FC<{
  title: string;
  value: number;
  isLoading: boolean;
  onClick?: () => void;
  testId?: string;
}> = ({ title, value, isLoading, onClick, testId }) => (
  <Card
    onClick={onClick}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    tabIndex={onClick ? 0 : undefined}
    role={onClick ? 'button' : undefined}
    aria-label={
      onClick ? `View document library, ${isLoading ? 'loading' : value} ${title}` : undefined
    }
    data-testid={testId}
    sx={{
      height: '100%',
      border: '1px solid #D9D9D9',
      borderRadius: '0.188rem',
      boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.15)',
      ...(onClick && {
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '-1px 2px 8px rgba(11, 147, 223, 0.25)',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }),
    }}
  >
    <CardContent>
      <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
        {isLoading ? '—' : value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const QueryStatsCards: React.FC<QueryStatsCardsProps> = ({
  stats,
  isLoading,
  onLibraryClick,
}) => (
  <Grid container spacing={3} sx={{ mb: 4 }}>
    <Grid item xs={12} sm={3}>
      <StatsCard
        title="Total Documents"
        value={stats.total}
        isLoading={isLoading}
        onClick={onLibraryClick}
        testId="docs-total-documents-card"
      />
    </Grid>
    <Grid item xs={12} sm={3}>
      <StatsCard title="Ready" value={stats.ready} isLoading={isLoading} />
    </Grid>
    <Grid item xs={12} sm={3}>
      <StatsCard title="Processing" value={stats.processing} isLoading={isLoading} />
    </Grid>
    <Grid item xs={12} sm={3}>
      <StatsCard title="Failed" value={stats.failed} isLoading={isLoading} />
    </Grid>
  </Grid>
);

export default QueryStatsCards;
